// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
  <v-card class="dtp">
    <!-- Заголовок: что это и зачем -->
    <div class="dtp-head">
      <div class="dtp-head-text">
        <div class="dtp-title">{{ $t('Time machine') }}</div>
        <div class="dtp-sub">{{ $t('See the sky as it was in the past or will be in the future') }}</div>
      </div>
      <div class="dtp-status" :class="{ 'dtp-status-live': isLive && !isTimePaused, 'dtp-status-paused': isTimePaused }">
        <span class="dtp-status-dot"></span>
        <span v-if="isTimePaused">{{ $t('Paused') }}</span>
        <span v-else-if="isLive">{{ $t('Live') }}</span>
        <span v-else>{{ $t('Custom time') }}</span>
      </div>
    </div>

    <!-- Дата и время -->
    <div class="dtp-fields">
      <div class="dtp-field">
        <div class="dtp-field-lbl">{{ $t('Date') }}</div>
        <div class="dtp-steppers">
          <div class="dtp-step" v-for="u in dateUnits" :key="u.unit">
            <button type="button" class="dtp-arrow" @mousedown="incTime(u.unit)" @touchstart.prevent="incTime(u.unit)"><v-icon small>mdi-chevron-up</v-icon></button>
            <span class="dtp-val">{{ u.value }}</span>
            <button type="button" class="dtp-arrow" @mousedown="decTime(u.unit)" @touchstart.prevent="decTime(u.unit)"><v-icon small>mdi-chevron-down</v-icon></button>
          </div>
        </div>
      </div>
      <div class="dtp-field">
        <div class="dtp-field-lbl">{{ $t('Time') }}</div>
        <div class="dtp-steppers">
          <div class="dtp-step" v-for="u in timeUnits" :key="u.unit">
            <button type="button" class="dtp-arrow" @mousedown="incTime(u.unit)" @touchstart.prevent="incTime(u.unit)"><v-icon small>mdi-chevron-up</v-icon></button>
            <span class="dtp-val">{{ u.value }}</span>
            <button type="button" class="dtp-arrow" @mousedown="decTime(u.unit)" @touchstart.prevent="decTime(u.unit)"><v-icon small>mdi-chevron-down</v-icon></button>
          </div>
        </div>
      </div>
    </div>

    <!-- Слайдер по ночи -->
    <div class="dtp-slider">
      <div class="dtp-slider-bg">
        <svg height="8" width="100%" preserveAspectRatio="none">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop v-for="stop in stops" :offset="stop.percent" :style="stop.style" :key="stop.percent"/>
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" rx="4" fill="url(#grad1)" />
        </svg>
      </div>
      <v-slider min="0" max="1439" class="dtp-range" hide-details color="#6ea8ff" track-color="transparent" v-model="timeMinute"></v-slider>
      <div class="dtp-slider-foot">
        <span>12:00</span>
        <span class="dtp-slider-hint">{{ sliderHint }}</span>
        <span>12:00</span>
      </div>
    </div>

    <!-- Действия -->
    <div class="dtp-actions">
      <button type="button" class="dtp-act" :class="{ 'dtp-act-on': isTimePaused }" @click="togglePauseTime">
        <v-icon small>{{ togglePauseTimeIcon }}</v-icon>
        <span class="dtp-act-text">
          <span class="dtp-act-title">{{ isTimePaused ? $t('Resume time') : $t('Pause time') }}</span>
          <span class="dtp-act-sub">{{ $t('Freeze the sky to explore it') }}</span>
        </span>
      </button>
      <button type="button" class="dtp-act" :disabled="isLive" @click="resetTime">
        <v-icon small>mdi-clock-outline</v-icon>
        <span class="dtp-act-text">
          <span class="dtp-act-title">{{ $t('Now') }}</span>
          <span class="dtp-act-sub">{{ $t('Back to real time') }}</span>
        </span>
      </button>
    </div>
  </v-card>
</template>

<script>

import Moment from 'moment'
var clickTimeout
var nbClickRepeat = 0

export default {
  data: function () {
    return {
      stops: [],
      stopCacheKey: {
        sliderStartTime: undefined,
        location: undefined
      }
    }
  },
  props: ['value', 'location'],
  computed: {
    // The MomentJS time in local time
    localTime: {
      get: function () {
        const m = Moment(this.value)
        m.local()
        return m
      },
      set: function (newValue) {
        this.$emit('input', newValue.format())
      }
    },
    time: {
      get: function () {
        return this.localTime.format('HH:mm:ss')
      }
    },
    date: {
      get: function () {
        return this.localTime.format('YYYY-MM-DD')
      }
    },
    timeMinute: {
      get: function () {
        // 0 means 12:00, 720 means midnight, 1440 (=24*60) means 12:00 the day after
        const t = this.localTime
        return t.hours() < 12 ? (t.hours() + 12) * 60 + t.minutes() : (t.hours() - 12) * 60 + t.minutes()
      },
      set: function (newValue) {
        var t = Moment(this.sliderStartTime)
        t.add(newValue, 'minutes')
        this.$emit('input', t.format())
      }
    },
    sliderStartTime: function () {
      const t = this.localTime.clone()
      if (t.hours() < 12) {
        t.subtract(1, 'days')
      }
      t.hours(12)
      t.minutes(0)
      t.seconds(0)
      t.milliseconds(0)
      return t
    },
    sliderHint: function () {
      const tm = this.timeMinute
      const stop = this.stops[Math.floor(tm * this.stops.length / 1440)]
      if (!stop) return ''
      if (stop.sunAlt > 0) {
        return this.$t('Daylight')
      }
      if (stop.sunAlt < -16) {
        return stop.moonAlt < 5 ? this.$t('Dark night') : this.$t('Moonlight')
      }
      return tm > 720 ? this.$t('Dawn') : this.$t('Twilight')
    },
    isTimePaused: function () {
      return this.$store.state.stel.time_speed === 0
    },
    togglePauseTimeIcon: function () {
      return this.isTimePaused ? 'mdi-play' : 'mdi-pause'
    },
    // Только представление: разбивка даты/времени на редактируемые части
    dateUnits: function () {
      const t = this.localTime
      return [
        { unit: 'years', value: t.format('YYYY') },
        { unit: 'months', value: t.format('MM') },
        { unit: 'days', value: t.format('DD') }
      ]
    },
    timeUnits: function () {
      const t = this.localTime
      return [
        { unit: 'hours', value: t.format('HH') },
        { unit: 'minutes', value: t.format('mm') },
        { unit: 'seconds', value: t.format('ss') }
      ]
    },
    // «Реальное время» = отличие от системных часов меньше минуты
    isLive: function () {
      return Math.abs(this.localTime.diff(Moment(), 'seconds')) < 60
    }
  },
  methods: {
    resetTime: function () {
      const m = Moment()
      m.local()
      this.$emit('input', m.format())
    },
    togglePauseTime: function () {
      this.$stel.core.time_speed = (this.$stel.core.time_speed === 0) ? 1 : 0
    },
    incTime: function (unit) {
      this.startIncTime(1, unit)
    },
    decTime: function (unit) {
      this.startIncTime(-1, unit)
    },
    startIncTime: function (v, unit) {
      const that = this
      clickTimeout = setTimeout(_ => {
        const t = this.localTime.clone()
        t.add(v, unit)
        this.$emit('input', t.format())
        nbClickRepeat++
        that.startIncTime(v, unit)
      }, nbClickRepeat === 0 ? 0 : (nbClickRepeat === 1 ? 500 : (nbClickRepeat < 10 ? 100 : (nbClickRepeat < 100 ? 50 : 20))))
    },
    stopIncTime: function () {
      if (clickTimeout) {
        clearTimeout(clickTimeout)
        clickTimeout = undefined
        nbClickRepeat = 0
      }
    },
    // 0 means 12:00, 720 means midnight, 1440 (=24*60) means 12:00 the day after
    timeMinuteRangeToUTC: function (tm) {
      return this.sliderStartTime.toDate().getMJD() + tm * 1 / (24 * 60)
    },
    refreshStops: function () {
      if (this.stopCacheKey.sliderStartTime === this.sliderStartTime.format() && this.stopCacheKey.location === JSON.stringify(this.location)) {
        return
      }
      const res = []
      const nbStop = 49
      const obs = this.$stel.core.observer.clone()
      const sun = this.$stel.getObj('NAME Sun')
      const moon = this.$stel.getObj('NAME Moon')
      for (let i = 0; i <= nbStop; ++i) {
        obs.utc = this.timeMinuteRangeToUTC(1440 * i / nbStop)
        const sunAlt = this.$stel.anpm(this.$stel.c2s(this.$stel.convertFrame(obs, 'ICRF', 'OBSERVED', sun.getInfo('radec', obs)))[1]) * 180.0 / Math.PI
        const moonAlt = this.$stel.anpm(this.$stel.c2s(this.$stel.convertFrame(obs, 'ICRF', 'OBSERVED', moon.getInfo('radec', obs)))[1]) * 180.0 / Math.PI
        const brightnessForAltitude = function (sunAlt, moonAlt) {
          const moonBrightness = moonAlt < 0 ? 0 : 2 / 35 * Math.min(20, moonAlt) / 20
          if (sunAlt > 0) return Math.min(10, 1 + sunAlt) + moonBrightness
          if (sunAlt < -16) return moonBrightness
          if (sunAlt < -10) return 1 / 35 * (16 + sunAlt) / 6 + moonBrightness
          return (1 - 1 / 35) * (10 + sunAlt) / 10 + 1 / 35 + moonBrightness
        }
        const brightness = Math.log10(1 + brightnessForAltitude(sunAlt, moonAlt) * 10) / 2
        res.push({
          percent: i / nbStop,
          style: 'stop-color:rgb(64,209,255);stop-opacity:' + brightness,
          sunAlt: sunAlt,
          moonAlt: moonAlt
        })
      }
      obs.destroy()
      this.stopCacheKey.sliderStartTime = this.sliderStartTime.format()
      this.stopCacheKey.location = JSON.stringify(this.location)
      this.stops = res
    }
  },
  mounted: function () {
    this.refreshStops()
    const that = this
    window.addEventListener('mouseup', function (event) {
      that.stopIncTime()
    })
    window.addEventListener('touchend', function (event) {
      that.stopIncTime()
    })
  },
  watch: {
    sliderStartTime: function () {
      this.refreshStops()
    },
    location: function () {
      this.refreshStops()
    }
  }
}
</script>

<style scoped>
.dtp {
  width: 400px;
  max-width: calc(100vw - 16px);
  background: #111a2e !important;
  border-radius: 16px !important;
  color: #eaf1ff;
}

/* Заголовок */
.dtp-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
}
.dtp-head-text { min-width: 0; }
.dtp-title { font-size: 16px; font-weight: 500; color: #eaf1ff; letter-spacing: 0.2px; }
.dtp-sub { font-size: 12px; color: #7f95bd; margin-top: 2px; line-height: 1.35; }
.dtp-status {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10px;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #b98bff;
  background: rgba(185, 139, 255, 0.12);
}
.dtp-status-dot { width: 6px; height: 6px; border-radius: 50%; background: #b98bff; }
.dtp-status-live { color: #6ea8ff; background: rgba(110, 168, 255, 0.12); }
.dtp-status-live .dtp-status-dot { background: #6ea8ff; }
.dtp-status-paused { color: #ffc46b; background: rgba(255, 196, 107, 0.12); }
.dtp-status-paused .dtp-status-dot { background: #ffc46b; }

/* Поля даты и времени */
.dtp-fields {
  display: flex;
  gap: 10px;
  padding: 4px 18px 0;
}
.dtp-field {
  flex: 1 1 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 8px 6px 6px;
}
.dtp-field-lbl {
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #6b80a6;
  text-align: center;
  margin-bottom: 2px;
}
.dtp-steppers { display: flex; justify-content: center; gap: 2px; }
.dtp-step { display: flex; flex-direction: column; align-items: center; }
.dtp-val {
  font-size: 20px;
  font-weight: 300;
  color: #f2f6ff;
  font-variant-numeric: tabular-nums;
  min-width: 34px;
  text-align: center;
  line-height: 1.1;
}
.dtp-step:first-child .dtp-val { min-width: 54px; }
.dtp-arrow {
  width: 100%;
  height: 22px;
  border: 0;
  background: transparent;
  color: #6b80a6;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}
.dtp-arrow:hover { background: rgba(110, 168, 255, 0.14); color: #9cc5ff; }
.dtp-arrow .v-icon { color: inherit !important; }

/* Слайдер */
.dtp-slider { position: relative; padding: 14px 18px 0; }
.dtp-slider-bg {
  position: absolute;
  left: 18px; right: 18px;
  top: 25px;
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
}
.dtp-slider-bg svg { display: block; width: 100%; }
.dtp-range { margin: 0; height: 30px; }
.dtp-range >>> .v-slider { margin: 0; }
.dtp-range >>> .v-slider__thumb { width: 16px; height: 16px; left: -8px; border: 2px solid #6ea8ff; background: #ffffff !important; }
.dtp-range >>> .v-slider__thumb::before { display: none; }
.dtp-slider-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: #56637f;
  font-variant-numeric: tabular-nums;
  margin-top: -6px;
}
.dtp-slider-hint { font-size: 11px; color: #9cc5ff; letter-spacing: 0.2px; }

/* Действия */
.dtp-actions {
  display: flex;
  gap: 10px;
  padding: 12px 18px 16px;
}
.dtp-act {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.04);
  color: #eaf1ff;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}
.dtp-act:hover { background: rgba(110, 168, 255, 0.12); border-color: rgba(110, 168, 255, 0.3); }
.dtp-act:disabled { opacity: 0.4; cursor: default; }
.dtp-act:disabled:hover { background: rgba(255, 255, 255, 0.04); border-color: rgba(255, 255, 255, 0.06); }
.dtp-act-on { border-color: rgba(255, 196, 107, 0.4); background: rgba(255, 196, 107, 0.1); }
.dtp-act .v-icon { color: #6ea8ff !important; flex: 0 0 auto; }
.dtp-act-on .v-icon { color: #ffc46b !important; }
.dtp-act-text { display: flex; flex-direction: column; min-width: 0; }
.dtp-act-title { font-size: 13px; font-weight: 500; line-height: 1.2; }
.dtp-act-sub { font-size: 10.5px; color: #7f95bd; line-height: 1.25; margin-top: 1px; }

/* Узкие экраны: поля и кнопки в столбик, компактные отступы */
@media (max-width: 420px) {
  .dtp-head { padding: 14px 14px 8px; }
  .dtp-fields { flex-direction: column; padding: 4px 14px 0; gap: 8px; }
  .dtp-field { padding: 8px 10px 6px; }
  .dtp-steppers { gap: 10px; }
  .dtp-val { font-size: 22px; }
  .dtp-slider { padding: 12px 14px 0; }
  .dtp-slider-bg { left: 14px; right: 14px; }
  .dtp-actions { flex-direction: column; gap: 8px; padding: 10px 14px 14px; }
  .dtp-arrow { height: 26px; }
}
</style>
